import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    Collapse
} from '@mui/material';
import {
    Edit,
    CalendarMonth,
    Share,
    ExpandMore,
    ExpandLess
} from '@mui/icons-material';
import {
    appointmentTypeShareService,
    MyAppointmentTypeShare,
    AppointmentTypeWithShares
} from '../../services/appointmentTypeShareService';
import ManageSharesDialog from './ManageSharesDialog';
import ManageReceivedSharesDialog from './ManageReceivedSharesDialog';
import ShareAllDialog from './ShareAllDialog';

interface GroupedShare {
    name: string;
    color: string;
    shares: MyAppointmentTypeShare[];
}

const AppointmentAccessTab: React.FC = () => {
    // Manageable appointment types state
    const [manageableTypes, setManageableTypes] = useState<AppointmentTypeWithShares[]>([]);
    const [loadingManageable, setLoadingManageable] = useState(true);

    // Received shares state
    const [receivedShares, setReceivedShares] = useState<MyAppointmentTypeShare[]>([]);
    const [loadingReceived, setLoadingReceived] = useState(true);
    const [receivedSectionExpanded, setReceivedSectionExpanded] = useState(true);

    // General state
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Dialog state
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareAllDialogOpen, setShareAllDialogOpen] = useState(false);
    const [selectedAppointmentType, setSelectedAppointmentType] = useState<AppointmentTypeWithShares | null>(null);
    const [receivedSharesDialogOpen, setReceivedSharesDialogOpen] = useState(false);
    const [selectedGroupedShare, setSelectedGroupedShare] = useState<GroupedShare | null>(null);

    // Group received shares by appointment type name
    const groupedReceivedShares = useMemo(() => {
        const groups = new Map<string, GroupedShare>();

        for (const share of receivedShares) {
            const existing = groups.get(share.name);
            if (existing) {
                existing.shares.push(share);
            } else {
                groups.set(share.name, {
                    name: share.name,
                    color: share.color,
                    shares: [share]
                });
            }
        }

        return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [receivedShares]);

    useEffect(() => {
        loadManageableTypes();
        loadReceivedShares();
    }, []);

    const loadManageableTypes = async () => {
        setLoadingManageable(true);
        try {
            const data = await appointmentTypeShareService.getManageableAppointmentTypes();
            setManageableTypes(data);
        } catch (err) {
            console.error('Error loading manageable types:', err);
            setError('Failed to load appointment types');
        } finally {
            setLoadingManageable(false);
        }
    };

    const loadReceivedShares = async () => {
        setLoadingReceived(true);
        try {
            const data = await appointmentTypeShareService.getMyShares();
            setReceivedShares(data);
        } catch (err) {
            console.error('Error loading received shares:', err);
        } finally {
            setLoadingReceived(false);
        }
    };

    const handleShareClick = (type: AppointmentTypeWithShares) => {
        setSelectedAppointmentType(type);
        setShareDialogOpen(true);
    };

    const handleShareDialogClose = () => {
        setShareDialogOpen(false);
        setSelectedAppointmentType(null);
    };

    const handleShareSuccess = () => {
        setSuccess('Sharing updated successfully');
        setTimeout(() => setSuccess(null), 3000);
        loadManageableTypes();
    };

    const handleShareAllSuccess = () => {
        setSuccess('All appointment types shared successfully');
        setTimeout(() => setSuccess(null), 3000);
        loadManageableTypes();
    };

    const getShareSummary = (type: AppointmentTypeWithShares): string => {
        if (type.isSharedWithAll) {
            return 'Shared with all';
        }
        if (type.sharedWith.length === 0) {
            return 'Not shared';
        }
        const names = type.sharedWith.map(s => s.fullName).join(', ');
        return `Shared with: ${names}`;
    };

    // Received shares handlers
    const handleReceivedShareEditClick = (group: GroupedShare) => {
        setSelectedGroupedShare(group);
        setReceivedSharesDialogOpen(true);
    };

    const handleReceivedShareRemoved = (shareId: string) => {
        setReceivedShares(prev => prev.filter(s => s.id !== shareId));
        // Update the selected group as well
        if (selectedGroupedShare) {
            const updatedShares = selectedGroupedShare.shares.filter(s => s.id !== shareId);
            if (updatedShares.length === 0) {
                setReceivedSharesDialogOpen(false);
                setSelectedGroupedShare(null);
            } else {
                setSelectedGroupedShare({
                    ...selectedGroupedShare,
                    shares: updatedShares
                });
            }
        }
        setSuccess('Share removed successfully');
        setTimeout(() => setSuccess(null), 3000);
    };

    const getSharedByText = (shares: MyAppointmentTypeShare[]): string => {
        if (shares.length === 1) {
            return `Shared by ${shares[0].sharedByUserName || 'Unknown'}`;
        }
        return `Shared by ${shares.length} users`;
    };

    if (loadingManageable && loadingReceived) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header with Share All button */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Appointment Sharing</Typography>
                {manageableTypes.length > 0 && (
                    <Button
                        variant="contained"
                        startIcon={<Share />}
                        onClick={() => setShareAllDialogOpen(true)}
                    >
                        Share All
                    </Button>
                )}
            </Box>

            <Typography variant="body2" color="text.secondary" paragraph>
                Manage sharing for your appointment types and view calendars shared with you.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Manageable Appointment Types Section */}
            {loadingManageable ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={24} />
                </Box>
            ) : manageableTypes.length === 0 ? (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box textAlign="center" py={4}>
                            <CalendarMonth sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                No Appointment Types
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                You don't have any appointment types to share yet.
                                Create appointment types in Business Settings to share them with others.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                <Card sx={{ mb: 3 }}>
                    <List>
                        {manageableTypes.map((type, index) => (
                            <ListItem
                                key={type.id}
                                divider={index < manageableTypes.length - 1}
                                sx={{ py: 2 }}
                            >
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '4px',
                                        backgroundColor: type.color || '#1976d2',
                                        mr: 2,
                                        flexShrink: 0
                                    }}
                                />
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1">
                                            {type.name}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography
                                            variant="body2"
                                            color={type.sharedWith.length === 0 ? 'text.disabled' : 'text.secondary'}
                                            sx={{ fontStyle: type.sharedWith.length === 0 ? 'italic' : 'normal' }}
                                        >
                                            {getShareSummary(type)}
                                        </Typography>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Edit />}
                                        onClick={() => handleShareClick(type)}
                                    >
                                        Edit
                                    </Button>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </Card>
            )}

            {/* Calendars Shared With Me Section 
            <Box sx={{ mt: 4 }}>
                <Box
                    onClick={() => setReceivedSectionExpanded(!receivedSectionExpanded)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        mb: 1,
                        '&:hover': { opacity: 0.8 }
                    }}
                >
                    {receivedSectionExpanded ? <ExpandLess /> : <ExpandMore />}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                        Calendars Shared With Me
                    </Typography>
                    {groupedReceivedShares.length > 0 && (
                        <Chip
                            label={groupedReceivedShares.length}
                            size="small"
                            sx={{ ml: 1 }}
                        />
                    )}
                </Box>

                <Collapse in={receivedSectionExpanded}>
                    {loadingReceived ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : groupedReceivedShares.length === 0 ? (
                        <Card>
                            <CardContent>
                                <Box textAlign="center" py={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        No calendars have been shared with you yet.
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card>
                            <List>
                                {groupedReceivedShares.map((group, index) => (
                                    <ListItem
                                        key={group.name}
                                        divider={index < groupedReceivedShares.length - 1}
                                        sx={{ py: 2 }}
                                    >
                                        <Box
                                            sx={{
                                                width: 16,
                                                height: 16,
                                                borderRadius: '4px',
                                                backgroundColor: group.color || '#1976d2',
                                                mr: 2,
                                                flexShrink: 0
                                            }}
                                        />
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle1">
                                                    {group.name}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography variant="body2" color="text.secondary">
                                                    {getSharedByText(group.shares)}
                                                </Typography>
                                            }
                                        />
                                        <ListItemSecondaryAction>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Edit />}
                                                onClick={() => handleReceivedShareEditClick(group)}
                                            >
                                                Edit
                                            </Button>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Card>
                    )}
                </Collapse>
            </Box>*/}

            {/* Share Dialog */}
            {selectedAppointmentType && (
                <ManageSharesDialog
                    open={shareDialogOpen}
                    onClose={handleShareDialogClose}
                    appointmentType={selectedAppointmentType}
                    onSuccess={handleShareSuccess}
                />
            )}

            {/* Share All Dialog */}
            <ShareAllDialog
                open={shareAllDialogOpen}
                onClose={() => setShareAllDialogOpen(false)}
                onSuccess={handleShareAllSuccess}
            />

            {/* Manage Received Shares Dialog */}
            {selectedGroupedShare && (
                <ManageReceivedSharesDialog
                    open={receivedSharesDialogOpen}
                    onClose={() => {
                        setReceivedSharesDialogOpen(false);
                        setSelectedGroupedShare(null);
                    }}
                    appointmentTypeName={selectedGroupedShare.name}
                    color={selectedGroupedShare.color}
                    shares={selectedGroupedShare.shares}
                    onShareRemoved={handleReceivedShareRemoved}
                />
            )}
        </Box>
    );
};

export default AppointmentAccessTab;
